'use client';

import { cn } from '@/lib/utils';
import { CheckIcon, ChevronDownIcon } from '@radix-ui/react-icons';
import * as SelectPrimitive from '@radix-ui/react-select';
import { HTMLAttributes, forwardRef } from 'react';
import styles from './dropdown.module.scss';

export type DropdownOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

export type DropdownProps = {
  options: DropdownOption[];
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  onValueChange?: (value: string) => void;
  className?: string;
} & HTMLAttributes<HTMLDivElement>;

export const Dropdown = forwardRef<HTMLDivElement, DropdownProps>(
  (
    {
      options,
      value,
      defaultValue,
      placeholder = 'Select an option...',
      disabled = false,
      onValueChange,
      className,
      ...props
    },
    ref,
  ) => {
    return (
      <div className={cn(styles.component, className)} ref={ref} {...props}>
        <SelectPrimitive.Root
          value={value}
          defaultValue={defaultValue}
          onValueChange={onValueChange}
          disabled={disabled}
        >
          <SelectPrimitive.Trigger className={styles.component_trigger}>
            <SelectPrimitive.Value placeholder={placeholder} className={styles.component_value} />
            <SelectPrimitive.Icon className={styles.component_icon}>
              <ChevronDownIcon />
            </SelectPrimitive.Icon>
          </SelectPrimitive.Trigger>

          <SelectPrimitive.Portal>
            <SelectPrimitive.Content
              className={styles.component_content}
              position="popper"
              sideOffset={4}
              align="start"
              style={{ width: 'var(--radix-select-trigger-width)' }}
            >
              <SelectPrimitive.Viewport className={styles.component_viewport}>
                {options.map((option) => (
                  <SelectPrimitive.Item
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                    className={styles.component_item}
                  >
                    <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                    <SelectPrimitive.ItemIndicator className={styles.component_item_indicator}>
                      <CheckIcon />
                    </SelectPrimitive.ItemIndicator>
                  </SelectPrimitive.Item>
                ))}
              </SelectPrimitive.Viewport>
            </SelectPrimitive.Content>
          </SelectPrimitive.Portal>
        </SelectPrimitive.Root>
      </div>
    );
  },
);

Dropdown.displayName = 'Dropdown';
